import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { canAccessPartnershipManagement } from './utils/partnershipPermissions';
import { canManageGallery } from './utils/galleryPermissions';
import { canAccessAttendanceLogs } from './utils/attendancePermissions';

// External Components
import Welcome from './components/external/Welcome';
import AboutUs from './components/external/AboutUs';
import Activity from './components/external/Activity';
import Achievement from './components/external/Achievement';
import Partner from './components/external/Partner';
import ContactUs from './components/external/ContactUs';

// Auth Components
import Login from './components/auth/Login';

// User Management
import UserList from './components/internal/user/UserList';
import UserForm from './components/internal/user/UserForm';
import UserDetails from './components/internal/user/UserDetails';

// Task Management
import TaskList from './components/internal/task/TaskList';
import TaskForm from './components/internal/task/TaskForm';
import TaskDetails from './components/internal/task/TaskDetails';   

// Event Management
import EventList from './components/internal/events/EventList';
import EventForm from './components/internal/events/EventForm';
import EventDetails from './components/internal/events/EventDetails';
import EventCalendar from './components/internal/events/EventCalendar';
import PenaltyList from './components/internal/events/PenaltyList';
import PenaltyForm from './components/internal/events/PenaltyForm';
import PenaltyDetails from './components/internal/events/PenaltyDetails';

//Document Management
import DocumentList from './components/internal/document/DocumentList';
import DocumentForm from './components/internal/document/DocumentForm';
import DocumentDetails from './components/internal/document/DocumentDetails'; 

// Multimedia Management
import Gallery from './components/internal/multimedia/GalleryList';
import GalleryForm from './components/internal/multimedia/GalleryForm';  
import GalleryDetails from './components/internal/multimedia/GalleryDetails'; 

// Financial Components
import ClaimList from './components/internal/financial/ClaimList';
import ClaimForm from './components/internal/financial/ClaimForm';
import ClaimDetails from './components/internal/financial/ClaimDetails';
import ClaimStripeReturn from './components/internal/financial/ClaimStripeReturn';
import BudgetList from './components/internal/financial/BudgetList';
import BudgetForm from './components/internal/financial/BudgetForm';
import BudgetDetails from './components/internal/financial/BudgetDetails';
import BudgetExpenses from './components/internal/financial/BudgetExpenses';
import MonthlyExpensesChart from './components/internal/financial/MonthlyExpensesChart';

// Attendance Components
import Attendance from './components/internal/attendance/Attendance';
import FaceAttendanceLogs from './components/internal/attendance/FaceAttendanceLogs';

// Public Components
import PublicAttendanceCheckIn from './components/public/PublicAttendanceCheckIn';

// Partnership Components
import CompanyList from './components/internal/partnership/CompanyList';
import CompanyDetails from './components/internal/partnership/CompanyDetails';
import CompanyForm from './components/internal/partnership/CompanyForm';
import RequirementList from './components/internal/partnership/RequirementList';
import RequirementDetails from './components/internal/partnership/RequirementDetails';
import RequirementForm from './components/internal/partnership/RequirementForm';
import PartnerList from './components/internal/partnership/PartnerList';
import PartnerDetails from './components/internal/partnership/PartnerDetails';
import PartnerForm from './components/internal/partnership/PartnerForm';
import SponsorTierList from './components/internal/partnership/SponsorTierList';
import SponsorTierForm from './components/internal/partnership/SponsorTierForm';
import SponsorTierDetails from './components/internal/partnership/SponsorTierDetails';

// Parcel Components
import ParcelList from './components/internal/purchasing/ParcelList';
import ParcelForm from './components/internal/purchasing/ParcelForm';
import ParcelDetails from './components/internal/purchasing/ParcelDetail';

// Maintenance Components
import MaintenanceList from './components/internal/maintenance/MaintenanceList';
import MaintenanceForm from './components/internal/maintenance/MaintenanceForm';
import MaintenanceDetails from './components/internal/maintenance/MaintenanceDetails';

// Stock Components
import StockList from './components/internal/inventory/StockList';
import StockForm from './components/internal/inventory/StockForm';
import StockDetails from './components/internal/inventory/StockDetails';
import BorrowList from './components/internal/inventory/BorrowList';
import BorrowForm from './components/internal/inventory/BorrowForm';
import BorrowDetails from './components/internal/inventory/BorrowDetails';

// Game Management
import AnalysisList from './components/internal/game/AnalysisList';
import AnalysisForm from './components/internal/game/AnalysisForm';
import AnalysisDetails from './components/internal/game/AnalysisDetails';

// General Department Components
import ManagementList from './components/internal/general/ManagementList';
import ManagementForm from './components/internal/general/ManagementForm';
import BureauList from './components/internal/general/BureauList';
import BureauForm from './components/internal/general/BureauForm';
import DepartmentList from './components/internal/general/DepartmentList';
import DepartmentForm from './components/internal/general/DepartmentForm';

// Layout Components
import ExternalLayout from './components/layout/ExternalLayout';
import InternalLayout from './components/layout/InternalLayout';
import InternalNotFound from './components/internal/common/InternalNotFound';
import InternalNoAccess from './components/internal/common/InternalNoAccess';
import Dashboard from './components/internal/Dashboard';
import AccessibilityView from './components/internal/AccessibilityView';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const getRoleFlags = (user) => {
  const isAdmin = Boolean(user?.is_admin);
  const isTL = Boolean(user?.is_team_leader) || user?.management_id === 1;
  const isHR = user?.management_id === 2;
  const isTreasurer = user?.management_id === 3;
  const isSecretary = user?.management_id === 4;
  const isPartner = user?.bureau_id === 1;
  /** Bureaus seeder: 2=Game Management, 3=Multimedia, 5=Purchasing, 6=Inventory */
  const isGame = user?.bureau_id === 2;
  const isMultimedia = user?.bureau_id === 3;
  const isPurchasing = user?.bureau_id === 5;
  const isInventory = user?.bureau_id === 6;

  return {
    // Raw flags
    isAdmin,
    isTL,
    isHR,
    isTreasurer,
    isSecretary,
    isPartner,
    isMultimedia,
    isPurchasing,
    isInventory,
    isGame,

    // Derived permissions (mirrors InternalLayout role checks)
    hasGenDept: isAdmin, // Admin only (Management, Bureau, Department)
    hasHR: isAdmin || isTL || isHR,
    hasBudget: isTL || isTreasurer,
    hasBudgetExp: isTL || isTreasurer,
    hasPartner: canAccessPartnershipManagement(user),
    hasMultimedia: canManageGallery(user),
    hasPurchasing: isAdmin || isTL || isPurchasing,
    hasGame: isAdmin || isTL || isGame,
    hasInv: isTL || isInventory,
  };
};

const isRestrictedAdminUser = (user) => (
  Boolean(user?.is_admin) && !isInactiveUser(user)
);

const isAllowedRestrictedAdminPath = (pathname) => (
  pathname === '/dashboard'
  || pathname === '/users'
  || pathname === '/users/add'
  || pathname.startsWith('/users/details/')
  || pathname.match(/^\/users\/[^/]+\/edit$/)
  || pathname === '/profile'
  || pathname === '/accessibility-view'
  || pathname === '/managements'
  || pathname === '/managements/add'
  || pathname.startsWith('/managements/edit/')
  || pathname.match(/^\/managements\/[^/]+$/)
  || pathname === '/bureaus'
  || pathname === '/bureaus/add'
  || pathname.startsWith('/bureaus/edit/')
  || pathname.match(/^\/bureaus\/[^/]+$/)
  || pathname === '/departments'
  || pathname === '/departments/add'
  || pathname.startsWith('/departments/edit/')
  || pathname.match(/^\/departments\/[^/]+$/)
);

const isInactiveUser = (user) => String(user?.status || 'active').toLowerCase() === 'inactive';
const isAllowedInactivePath = (pathname) => (
  pathname === '/galleries' || pathname.startsWith('/galleries/')
);

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
};

const RestrictedAdminGuard = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();

  if (loading) return null;
  if (!user) return <Navigate to="/login" />;

  if (isRestrictedAdminUser(user) && !isAllowedRestrictedAdminPath(pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const InactiveGalleryGuard = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();

  if (loading) return null;
  if (!user) return <Navigate to="/login" />;

  if (isInactiveUser(user) && !isAllowedInactivePath(pathname)) {
    return <Navigate to="/galleries" replace />;
  }

  return children;
};

const FeatureRoute = ({ feature, children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" />;

  const flags = getRoleFlags(user);

  const access = (() => {
    switch (feature) {
      case 'accessibility':
      case 'admin':
        return { canAccess: flags.isAdmin, requiredRole: 'Admin' };
      case 'general.department':
        return { canAccess: flags.hasGenDept, requiredRole: 'Admin' };
      case 'user.management':
        return { canAccess: flags.hasHR, requiredRole: 'Admin, Team Leader, or HR' };
      case 'documents':
        return { canAccess: true, requiredRole: null };
      case 'maintenance':
        return { canAccess: true, requiredRole: null };
      case 'parcels':
        return { canAccess: true, requiredRole: null };
      case 'partnership':
        return { canAccess: flags.hasPartner, requiredRole: 'Team Leader or Partner Bureau' };
      case 'inventory':
        return { canAccess: flags.hasInv, requiredRole: 'Team Leader or Inventory Bureau' };
      case 'budget':
        return { canAccess: flags.hasBudget, requiredRole: 'Team Leader or Treasurer' };
      case 'budget.expenses':
        return { canAccess: flags.hasBudgetExp, requiredRole: 'Team Leader or Treasurer' };
      case 'multimedia':
        return { canAccess: flags.hasMultimedia, requiredRole: 'Team Leader or Multimedia bureau' };
      case 'game':
        return { canAccess: flags.hasGame, requiredRole: 'Team Leader or Game Management bureau' };
      case 'analyses':
        return { canAccess: true, requiredRole: null };
      case 'claims':
        return { canAccess: true, requiredRole: null };
      default:
        return { canAccess: true, requiredRole: null };
    }
  })();

  if (!access.canAccess) {
    return <InternalNoAccess requiredRole={access.requiredRole} />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) {
    return <Navigate to="/login" />;
  }

  const flags = getRoleFlags(user);
  const canManageUsers = flags.hasHR;
  
  if (!canManageUsers) {
    return <InternalNoAccess requiredRole="Admin, Team Leader, or HR" />;
  }
  
  return children;
};

const AttendanceLogsRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!canAccessAttendanceLogs(user)) {
    return <InternalNoAccess requiredRole="Team Leader or HR" />;
  }

  return children;
};

// Admin only (Management, Bureau, Department).
const GeneralAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) {
    return <Navigate to="/login" />;
  }

  const flags = getRoleFlags(user);
  const canAccess = flags.hasGenDept;

  if (!canAccess) {
    return <InternalNoAccess requiredRole="Admin" />;
  }

  return children;
};

const App = () => {
  return (
    <AuthProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Routes>
            {/* External Routes */}
            <Route element={<ExternalLayout />}>
              <Route path="/" element={<Welcome />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/activities" element={<Activity />} />
              <Route path="/achievements" element={<Achievement />} />
              <Route path="/partners/public" element={<Partner />} />
              <Route path="/contact" element={<ContactUs />} />
            </Route>

            {/* Public Routes (No Layout) */}
            <Route path="/attendance/face-scan" element={<PublicAttendanceCheckIn />} />
            <Route path="/attendance/check-in" element={<Navigate to="/attendance/face-scan" replace />} />

            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />

            {/* Internal Routes */}
            <Route
              element={
                <PrivateRoute>
                  <InactiveGalleryGuard>
                    <RestrictedAdminGuard>
                      <InternalLayout />
                    </RestrictedAdminGuard>
                  </InactiveGalleryGuard>
                </PrivateRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route
                path="/accessibility-view"
                element={
                  <AdminRoute>
                    <AccessibilityView />
                  </AdminRoute>
                }
              />
              <Route path="/users" element={<UserList />}/>
              <Route path="/users/details/:id" element={<UserDetails />}/>
              <Route path="/profile" element={<UserDetails />} />
              <Route path="/users/:id/edit" element={ <UserForm isEdit={true} />}/>
              <Route
                path="/users/add"
                element={
                  <AdminRoute>
                    <UserForm />
                  </AdminRoute>
                }
              />
              
              <Route path="/tasks" element={<TaskList />} />
              <Route path="/tasks/create" element={<TaskForm />} />
              <Route path="/tasks/edit/:id" element={<TaskForm isEdit={true} />} />
              <Route path="/tasks/:id" element={<TaskDetails />} />

              <Route
                path="/documents"
                element={
                  <FeatureRoute feature="documents">
                    <DocumentList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/documents/add"
                element={
                  <FeatureRoute feature="documents">
                    <DocumentForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/documents/edit/:id"
                element={
                  <FeatureRoute feature="documents">
                    <DocumentForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/documents/:id"
                element={
                  <FeatureRoute feature="documents">
                    <DocumentDetails />
                  </FeatureRoute>
                }
              />

              <Route path="/events" element={<EventList />} />
              <Route path="/events/create" element={<EventForm />} />
              <Route path="/events/edit/:id" element={<EventForm isEdit={true} />} />
              <Route path="/events/view/:id" element={<EventDetails />} />

              <Route path="/calendar" element={<EventCalendar />} />

              <Route
                path="/galleries"
                element={
                  <Gallery />
                }
              />
              <Route
                path="/galleries/add"
                element={
                  <FeatureRoute feature="multimedia">
                    <GalleryForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/galleries/edit/:id"
                element={
                  <FeatureRoute feature="multimedia">
                    <GalleryForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/galleries/:id"
                element={
                  <GalleryDetails />
                }
              />
              
              <Route path="/attendance" element={<Attendance />} />
              <Route
                path="/attendance/logs"
                element={
                  <AttendanceLogsRoute>
                    <FaceAttendanceLogs />
                  </AttendanceLogsRoute>
                }
              />

              <Route
                path="/companies"
                element={
                  <FeatureRoute feature="partnership">
                    <CompanyList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/companies/add"
                element={
                  <FeatureRoute feature="partnership">
                    <CompanyForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/companies/edit/:id"
                element={
                  <FeatureRoute feature="partnership">
                    <CompanyForm isEdit={true} />
                  </FeatureRoute>
                }
              />
              <Route
                path="/companies/:id"
                element={
                  <FeatureRoute feature="partnership">
                    <CompanyDetails />
                  </FeatureRoute>
                }
              />

              <Route
                path="/partners"
                element={
                  <FeatureRoute feature="partnership">
                    <PartnerList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/partners/add"
                element={
                  <FeatureRoute feature="partnership">
                    <PartnerForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/partners/edit/:id"
                element={
                  <FeatureRoute feature="partnership">
                    <PartnerForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/partners/:id"
                element={
                  <FeatureRoute feature="partnership">
                    <PartnerDetails />
                  </FeatureRoute>
                }
              />

              <Route
                path="/sponsor-tiers"
                element={
                  <FeatureRoute feature="partnership">
                    <SponsorTierList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/sponsor-tiers/add"
                element={
                  <FeatureRoute feature="partnership">
                    <SponsorTierForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/sponsor-tiers/edit/:id"
                element={
                  <FeatureRoute feature="partnership">
                    <SponsorTierForm isEdit />
                  </FeatureRoute>
                }
              />
              <Route
                path="/sponsor-tiers/:id"
                element={
                  <FeatureRoute feature="partnership">
                    <SponsorTierDetails />
                  </FeatureRoute>
                }
              />

              <Route path="/requirements" element={<RequirementList />} />
              <Route path="/requirements/add" element={<RequirementForm />} />
              <Route path="/requirements/edit/:id" element={<RequirementForm isEdit={true} />} />
              <Route path="/requirements/:id" element={<RequirementDetails />} />
              
              <Route
                path="/parcels"
                element={
                  <FeatureRoute feature="parcels">
                    <ParcelList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/parcels/add"
                element={
                  <FeatureRoute feature="parcels">
                    <ParcelForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/parcels/edit/:id"
                element={
                  <FeatureRoute feature="parcels">
                    <ParcelForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/parcels/:id"
                element={
                  <FeatureRoute feature="parcels">
                    <ParcelDetails />
                  </FeatureRoute>
                }
              />
              
              <Route
                path="/maintenances"
                element={
                  <FeatureRoute feature="maintenance">
                    <MaintenanceList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/maintenances/add"
                element={
                  <FeatureRoute feature="maintenance">
                    <MaintenanceForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/maintenances/:id"
                element={
                  <FeatureRoute feature="maintenance">
                    <MaintenanceDetails />
                  </FeatureRoute>
                }
              />
              <Route
                path="/maintenances/:id/edit"
                element={
                  <FeatureRoute feature="maintenance">
                    <MaintenanceForm />
                  </FeatureRoute>
                }
              />
              
              <Route path="/penalties" element={<PenaltyList />} />
              <Route path="/penalties/add" element={<PenaltyForm isPage={true} />} />
              <Route path="/penalties/edit/:id" element={<PenaltyForm isPage={true} />} />
              <Route path="/penalties/:id" element={<PenaltyDetails />} />

              <Route
                path="/claims"
                element={
                  <FeatureRoute feature="claims">
                    <ClaimList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/claims/add"
                element={
                  <FeatureRoute feature="claims">
                    <ClaimForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/claims/edit/:id"
                element={
                  <FeatureRoute feature="claims">
                    <ClaimForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/claims/:id/stripe-return"
                element={
                  <FeatureRoute feature="claims">
                    <ClaimStripeReturn />
                  </FeatureRoute>
                }
              />
              <Route
                path="/claims/:id"
                element={
                  <FeatureRoute feature="claims">
                    <ClaimDetails />
                  </FeatureRoute>
                }
              />

              <Route
                path="/stocks"
                element={
                  <FeatureRoute feature="inventory">
                    <StockList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/stocks/add"
                element={
                  <FeatureRoute feature="inventory">
                    <StockForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/stocks/edit/:id"
                element={
                  <FeatureRoute feature="inventory">
                    <StockForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/stocks/:id"
                element={
                  <FeatureRoute feature="inventory">
                    <StockDetails />
                  </FeatureRoute>
                }
              />
              
              <Route
                path="/borrows"
                element={
                  <FeatureRoute feature="inventory">
                    <BorrowList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/borrows/add"
                element={
                  <FeatureRoute feature="inventory">
                    <BorrowForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/borrows/edit/:id"
                element={
                  <FeatureRoute feature="inventory">
                    <BorrowForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/borrows/:id"
                element={
                  <FeatureRoute feature="inventory">
                    <BorrowDetails />
                  </FeatureRoute>
                }
              />

              <Route
                path="/budgets"
                element={
                  <FeatureRoute feature="budget">
                    <BudgetList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/budgets/add"
                element={
                  <FeatureRoute feature="budget">
                    <BudgetForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/budgets/edit/:id"
                element={
                  <FeatureRoute feature="budget">
                    <BudgetForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/budgets/:id"
                element={
                  <FeatureRoute feature="budget">
                    <BudgetDetails />
                  </FeatureRoute>
                }
              />
              <Route
                path="/budget-expenses"
                element={
                  <FeatureRoute feature="budget.expenses">
                    <BudgetExpenses />
                  </FeatureRoute>
                }
              />
              <Route
                path="/budgets/monthly-totals"
                element={
                  <FeatureRoute feature="budget">
                    <MonthlyExpensesChart />
                  </FeatureRoute>
                }
              />

              <Route
                path="/analyses"
                element={
                  <FeatureRoute feature="analyses">
                    <AnalysisList />
                  </FeatureRoute>
                }
              />
              <Route
                path="/analyses/add"
                element={
                  <FeatureRoute feature="game">
                    <AnalysisForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/analyses/edit/:id"
                element={
                  <FeatureRoute feature="game">
                    <AnalysisForm />
                  </FeatureRoute>
                }
              />
              <Route
                path="/analyses/:id"
                element={
                  <FeatureRoute feature="analyses">
                    <AnalysisDetails />
                  </FeatureRoute>
                }
              />

              <Route
                path="/managements"
                element={
                  <GeneralAdminRoute>
                    <ManagementList />
                  </GeneralAdminRoute>
                }
              />
              <Route
                path="/managements/add"
                element={
                  <GeneralAdminRoute>
                    <ManagementForm />
                  </GeneralAdminRoute>
                }
              />
              <Route
                path="/managements/edit/:id"
                element={
                  <GeneralAdminRoute>
                    <ManagementForm />
                  </GeneralAdminRoute>
                }
              />
              <Route
                path="/managements/:id"
                element={
                  <GeneralAdminRoute>
                    <ManagementForm isDetails />
                  </GeneralAdminRoute>
                }
              />

              <Route
                path="/bureaus"
                element={
                  <GeneralAdminRoute>
                    <BureauList />
                  </GeneralAdminRoute>
                }
              />
              <Route
                path="/bureaus/add"
                element={
                  <GeneralAdminRoute>
                    <BureauForm />
                  </GeneralAdminRoute>
                }
              />
              <Route
                path="/bureaus/edit/:id"
                element={
                  <GeneralAdminRoute>
                    <BureauForm />
                  </GeneralAdminRoute>
                }
              />
              <Route
                path="/bureaus/:id"
                element={
                  <GeneralAdminRoute>
                    <BureauForm isDetails />
                  </GeneralAdminRoute>
                }
              />

              <Route
                path="/departments"
                element={
                  <GeneralAdminRoute>
                    <DepartmentList />
                  </GeneralAdminRoute>
                }
              />
              <Route
                path="/departments/add"
                element={
                  <GeneralAdminRoute>
                    <DepartmentForm />
                  </GeneralAdminRoute>
                }
              />
              <Route
                path="/departments/edit/:id"
                element={
                  <GeneralAdminRoute>
                    <DepartmentForm />
                  </GeneralAdminRoute>
                }
              />
              <Route
                path="/departments/:id"
                element={
                  <GeneralAdminRoute>
                    <DepartmentForm isDetails />
                  </GeneralAdminRoute>
                }
              />
              <Route path='*' element={<InternalNotFound />} />
            </Route>
          </Routes>
        </Box>
      </Router>
    </ThemeProvider>
    </AuthProvider>
  );
};

export default App; 
